import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { BadgeCheck, ShieldCheck, Clock, AlertTriangle, ExternalLink } from "lucide-react";

const LOGO_URL = "/logo.png";

function fmtCountdown(expiresAt) {
  try {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return "Expired";
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days >= 1) return `Expires in ${days} day${days === 1 ? "" : "s"}`;
    if (hours >= 1) return `Expires in ${hours} hour${hours === 1 ? "" : "s"}`;
    const mins = Math.max(1, Math.floor(ms / (1000 * 60)));
    return `Expires in ${mins} min`;
  } catch {
    return "";
  }
}

export default function ShareView() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let revoked = false;
    let createdUrl = null;
    (async () => {
      try {
        const { data } = await api.get(`/share/${token}/meta`);
        if (revoked) return;
        setMeta(data);
        // Fetch the file as blob so we can render it inline (works for PDF and images)
        try {
          const res = await api.get(`/share/${token}`, { responseType: "blob" });
          if (revoked) return;
          createdUrl = URL.createObjectURL(res.data);
          setBlobUrl(createdUrl);
        } catch (e) {
          // file fetch failed but meta was OK; show meta only
        }
      } catch (e) {
        if (revoked) return;
        const status = e.response?.status;
        if (status === 410) setError(formatApiError(e.response?.data?.detail) || "This link is no longer valid.");
        else if (status === 404) setError("This share link doesn't exist.");
        else setError(formatApiError(e.response?.data?.detail) || "Unable to load this document.");
      } finally {
        if (!revoked) setLoading(false);
      }
    })();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [token]);

  const isPdf = (meta?.content_type || "").includes("pdf");
  const isImage = (meta?.content_type || "").startsWith("image/");

  return (
    <div className="bg-peach min-h-screen" data-testid="share-view-page">
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-14">
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-black/5">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Rate-ED" className="h-14 w-auto object-contain" />
              <div>
                <div className="font-display text-2xl font-bold uppercase" style={{ color: "#13A699" }} data-testid="share-title">
                  Mentor Document
                </div>
                <div className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "#13A69999" }}>
                  <ShieldCheck className="h-3.5 w-3.5" /> Verified by Rate-ED
                </div>
              </div>
            </div>
            {meta && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase px-3 py-1.5 rounded-full"
                style={{ background: "#FFD708", color: "#13A699" }}
                data-testid="share-expiry-badge"
              >
                <Clock className="h-3.5 w-3.5" /> {fmtCountdown(meta.expires_at)}
              </span>
            )}
          </div>

          {loading && (
            <p className="mt-6 text-sm" style={{ color: "#13A699" }} data-testid="share-loading">
              Loading document…
            </p>
          )}

          {error && (
            <div
              className="mt-6 rounded-2xl p-4 flex items-start gap-3"
              style={{ background: "#FEE2E2", border: "1px solid #DC2626" }}
              data-testid="share-error"
            >
              <AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: "#DC2626" }} />
              <div>
                <div className="font-bold" style={{ color: "#DC2626" }}>This link is no longer accessible</div>
                <p className="text-sm mt-1" style={{ color: "#7F1D1D" }}>{error}</p>
                <Link to="/" className="inline-block mt-3 text-xs font-bold underline" style={{ color: "#13A699" }}>
                  Go to Rate-ED Home →
                </Link>
              </div>
            </div>
          )}

          {meta && !error && (
            <>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="h-5 w-5" fill="#13A699" stroke="#FFF7ED" />
                  <div className="text-sm" style={{ color: "#13A699" }}>
                    Shared by <b data-testid="share-owner-name">{meta.owner_name}</b>
                    {meta.doc_type && <> · {meta.doc_type === "degree" ? "Degree Certificate" : meta.doc_type}</>}
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-black/5 overflow-hidden bg-[#FFF7ED]" data-testid="share-preview">
                {!blobUrl ? (
                  <div className="p-8 text-center text-sm" style={{ color: "#13A699" }}>
                    Preview unavailable.{" "}
                    <a
                      href={`${API}/share/${token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold underline inline-flex items-center gap-1"
                      data-testid="share-open-link"
                    >
                      Open in new tab <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                ) : isPdf ? (
                  <iframe
                    src={blobUrl}
                    title="Shared document"
                    className="w-full"
                    style={{ height: "70vh" }}
                    data-testid="share-iframe"
                  />
                ) : isImage ? (
                  <div className="flex items-center justify-center p-4">
                    <img
                      src={blobUrl}
                      alt="Shared document"
                      loading="lazy"
                      decoding="async"
                      className="max-h-[70vh] w-auto object-contain"
                      data-testid="share-image"
                    />
                  </div>
                ) : (
                  <div className="p-8 text-center text-sm" style={{ color: "#13A699" }}>
                    <a
                      href={blobUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold underline inline-flex items-center gap-1"
                      data-testid="share-open-blob"
                    >
                      Open document <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </div>

              <p className="mt-4 text-[11px]" style={{ color: "#13A69999" }}>
                This is a temporary share link. The full document remains private and is not indexed by search engines.
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
